import { Injectable } from "@angular/core";
import { PaginationType } from "../shared/models/pagination.model";
import { ApiService } from "./api.service";

// workgroup.service.ts
@Injectable({ providedIn: 'root' })
export class RosterService {
  private controller = '/roster';

  constructor (private apiService: ApiService) { }

  getAll() {
    return this.apiService.get(this.controller);
  }

  getRosterStatusCounts(keyword: string = '', tagIds: string[] = []) {
    return this.apiService.post(`${this.controller}/roster-status-counts`, { keyword, tagIds });
  }

  getNonArchivedRosters() {
    return this.apiService.post(`${this.controller}/get-non-archived-rosters`, {});
  }

  getRosters(keyword: string, pagination: PaginationType, status: string = '', tagIds: string[] = []) {
    const model = {
      keyword,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortField: pagination.sortField,
      sortDir: pagination.sortDir,
      status,
      tagIds,
    }
    return this.apiService.post(`${this.controller}/get-rosters`, model);
  }

  getRosterTags(rosterId: string) {
    return this.apiService.get(`${this.controller}/${rosterId}/tags`);
  }

  assignRosterTags(rosterId: string, tagIds: string[]) {
    return this.apiService.post(`${this.controller}/${rosterId}/tags`, { tagIds });
  }

  getRosterCalendar() {
    return this.apiService.get(`${this.controller}/roster-calendar`);
  }

  // getRosterCalendarByDate(startDate: string, endDate: string) {
  //     return this.apiService.get(`${this.controller}/roster-calendar-filter/${startDate}/${endDate}`);
  // }

  getRosterCalendarByDate(model: any) {
    return this.apiService.post(`${this.controller}/roster-calendar-filter`, model);
  }

  getTodayRosterCalendar() {
    return this.apiService.get(`${this.controller}/roster-calendar-filter/user`);
  }


  get(id: string) {
    return this.apiService.get(`${this.controller}/${id}`);
  }

  create(roster: any) {
    const body = {
      ...roster,
      weekdays: roster.weekdays.map((d: any) => ({ weekDay: d }))
    };
    return this.apiService.post(this.controller, body);
  }

  update(id: string, roster: any) {
    const body = {
      ...roster,
      weekdays: roster.weekdays.map((d: any) => ({ weekDay: d }))
    };
    return this.apiService.put(`${this.controller}/${id}`, body);
  }

  delete(id: string, forceUnassign = false) {
    return this.apiService.delete(`${this.controller}/${id}?forceUnassign=${forceUnassign}`);
  }

  // Preview shown before Delete on the Roster List (New tab) — who's currently assigned, and whether
  // deletion is blocked outright by real attendance data (see RosterController.GetRosterAssignedStaff).
  getAssignedStaff(id: string) {
    return this.apiService.get(`${this.controller}/${id}/assigned-staff`);
  }

  getAvailableStaffs(rosterId: string, shiftId: string) {
    return this.apiService.get(`${this.controller}/staff/available/${rosterId}/${shiftId}`);
  }

  getRosterAvailableStaffs(model: any) {
    return this.apiService.post(`${this.controller}/user/available`, model);
  }

  getAddedStaffs(rosterId: string, shiftId: string) {
    return this.apiService.get(`${this.controller}/staff/added/${rosterId}/${shiftId}`);
  }

  getAddedStaffsByGroup(rosterId: string) {
    return this.apiService.get(`${this.controller}/staff/added/${rosterId}`);
  }

  getAssignedStaffsByShiftSnapshot(shiftSnapshotId: string) {
    return this.apiService.get(`${this.controller}/staff/assigned/${shiftSnapshotId}`);
  }

  getRosterStaffs(model: any) {
    return this.apiService.post(`${this.controller}/staff/added`, model);
  }

  getAddedStaffsByRosterAndDate(rosterId: string, date: string) {
    return this.apiService.get(`${this.controller}/staff/added-by-date/${rosterId}/${date}`);
  }

  marrkOffline(rosterId: string, shiftId: string, userId: string, date: string) {
    return this.apiService.post(`${this.controller}/absent/${rosterId}/${shiftId}/${userId}/${date}`);
  }

  assignBackup(rosterId: string, shiftId: string, originalUserId: string, coveringUserId: string, date: string) {
    return this.apiService.post(`${this.controller}/backup/${rosterId}/${shiftId}/${originalUserId}/${coveringUserId}/${date}`);
  }

  addUserToRoster(rosterId: string, shiftId: string, userId: string) {
    return this.apiService.post(`${this.controller}/user/add/${rosterId}/${shiftId}/${userId}`);
  }

  addUserToRosterV2(model: any) {
    return this.apiService.post(`${this.controller}/user/add/v2`, model);
  }

  removeUserFromRoster(rosterId: string, shiftId: string, userId: string) {
    return this.apiService.post(`${this.controller}/user/remove/${rosterId}/${shiftId}/${userId}`);
  }

  doEmergency(model: any) {
    return this.apiService.post(`/eventKnight/emegency`, model);
  }

  updateEmergency(userId: string, isResolved: boolean) {
    return this.apiService.post(`/eventKnight/emegency/update/${userId}/${isResolved}`); // /eventKnight/emegency/update/{userId}/{isResolved}
  }

  filterRoster(model: any) {
    return this.apiService.post(`${this.controller}/filter`, model);
  }

  getReportSummary(model: any) {
    return this.apiService.post(`${this.controller}/report/summary`, model);
  }

  markAsLeader(rosterId : string, shiftId: string, userId: string, isAdd: boolean) {
    return this.apiService.post(`${this.controller}/user/leader/${rosterId}/${shiftId}/${userId}/${isAdd}`);
  }

  changeUserAlertStatus(rosterId: string, shiftId: string, userId: string, isEnableAlert: boolean) {
    return this.apiService.post(`${this.controller}/user/alert/${rosterId}/${shiftId}/${userId}?isEnableAlert=${isEnableAlert}`);
  }

  swapUser(rosterId: string, shiftId: string, originalUserId: string, newUserId: string) {
    return this.apiService.post(`${this.controller}/user/swap/${rosterId}/${shiftId}/${originalUserId}/${newUserId}`); // /eventKnight/emegency/update/{userId}/{isResolved}
  }

  getUsersActivePeriod(model: any) {
    return this.apiService.post(`${this.controller}/users/active-period`, model);
  }

  getGeofence(id: string) {
    return this.apiService.get(`${this.controller}/geofence/${id}`);
  }

  getAuditLogs(rosterId: string, auditKeyword: string, auditType: string, pagination: PaginationType) {
    const model = {
      rosterId: rosterId,
      keyword: auditKeyword,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      type: auditType,
    };
    return this.apiService.post(`${this.controller}/${rosterId}/audit-logs`, model);
  }

  getShiftSnapshots(rosterId: string) {
    return this.apiService.get(`${this.controller}/${rosterId}/shift-snapshots`);
  }

  syncSnapshot(rosterId: string, model: any) {
    return this.apiService.post(`${this.controller}/${rosterId}/sync-snapshot`, model);
  }

  getSnapshotDrift(rosterId: string) {
    return this.apiService.get(`${this.controller}/${rosterId}/snapshot-drift`);
  }

  getActiveShiftSnapshots(rosterSnapshotId: string) {
    return this.apiService.get(`${this.controller}/active-shift-snapshot/${rosterSnapshotId}`);
  }

  getActiveRosterSnapshots(assigmentId: string) {
    return this.apiService.get(`${this.controller}/active-roster-snapshots/${assigmentId}`);
  }

  getSnapshotSummary(rosterId: string) {
    return this.apiService.get(`${this.controller}/${rosterId}/snapshot-summary`);
  }

  deleteSnapshot(snapshotId: string) {
    return this.apiService.delete(`${this.controller}/snapshot/${snapshotId}`);
  }

  createStandbyRoster(model: any) {
    return this.apiService.post('/newroster', model);
  }

  getStandbySnapshots(type: string) {
    return this.apiService.get(`${this.controller}/standby-snapshots?type=${type}`);
  }
}
